import { useContext } from 'react'
import type { AppDispatch } from '@redux/store'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as trackingLinkActions from '@redux/modules/trackingLink'

import defaultFormState, { requiredFields } from '@models/trackingLink'

import PageContext from '@contexts/pageContext'

import type { DeleteParams } from '@redux/modules/types'
import type { TrackingLinkModel, TrackingLinkRequestOptions } from '@domain-group/realhub-backend-api-types'

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: TrackingLinkRequestOptions,
}

export const generateUrls = (trackingLink?: Partial<TrackingLinkModel>) => {
  const { id } = trackingLink || {}

  return {
    editTrackingLinkDetailsUrl: `#/sectionName/trackingLinks/${id}/edit/details`,
    editTrackingLinkUrl: `#/sectionName/trackingLinks/${id}/edit/`,
    trackingLinksIndexUrl: '#/sectionName/trackingLinks',
  }
}

type CreateTrackingLinkParams = {
  dispatch: AppDispatch,
  trackingLinkParams: Partial<TrackingLinkModel>,
  requestOptions?: TrackingLinkRequestOptions,
}

const createTrackingLink = (params: CreateTrackingLinkParams) => {
  const { trackingLinkParams, dispatch, requestOptions } = params
  const { createTrackingLink: createFn } = trackingLinkActions

  return dispatch(createFn(trackingLinkParams, requestOptions))
}

type UpdateTrackingLinkParams = {
  dispatch: AppDispatch,
  trackingLink: Partial<TrackingLinkModel>,
  trackingLinkParams: Partial<TrackingLinkModel>,
  requestOptions?: TrackingLinkRequestOptions,
}

const updateTrackingLink = (params: UpdateTrackingLinkParams) => {
  const {
    dispatch, trackingLink, trackingLinkParams, requestOptions,
  } = params
  const { updateTrackingLink: updateFn } = trackingLinkActions

  const updatedParams = {
    id: trackingLink.id,
    ...trackingLinkParams,
  }

  // @ts-expect-error TS2345
  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTrackingLinkParams = {
  dispatch: AppDispatch,
  trackingLink: DeleteParams<TrackingLinkModel>,
}

const deleteTrackingLink = (params: DeleteTrackingLinkParams) => {
  const { trackingLink, dispatch } = params
  const { deleteTrackingLink: deleteFn } = trackingLinkActions

  return dispatch(deleteFn(trackingLink))
}

export type ShowCreateOrEditTrackingLinkModalPayload = {
  trackingLink: Partial<TrackingLinkModel>,
}

type CreateOrEditTrackingLinkParams = {
  trackingLink: Partial<TrackingLinkModel>,
  showCreateOrEditTrackingLinkModal: (payload: ShowCreateOrEditTrackingLinkModalPayload) => void,
}

const createOrEditTrackingLink = (params: CreateOrEditTrackingLinkParams) => new Promise((resolve, reject) => {
  const { trackingLink, showCreateOrEditTrackingLinkModal } = params

  if (showCreateOrEditTrackingLinkModal){
    const payload = {
      trackingLink,
    }

    showCreateOrEditTrackingLinkModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditTrackingLinkModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTrackingLinkForm(
  trackingLink: Partial<TrackingLinkModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const trackingLinkForm = useForm(
    defaultFormState,
    { entity: trackingLink, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [trackingLink.id, trackingLink.cache_key],
  )

  return {
    ...trackingLinkForm,
  }
}

// Return related entities
export const useRelations = (entity: Partial<TrackingLinkModel> = {}) => {
  // @ts-expect-error TS2339
  const { related_entity_id } = entity

  const { relatedEntities } = useSelector(reduxState => reduxState.entities)

  const relatedEntity = related_entity_id && relatedEntities[related_entity_id] ? relatedEntities[related_entity_id] : {}

  return {
    relatedEntity,
  }
}

// @ts-expect-error TS1015
function useTrackingLink(initEntity: Partial<TrackingLinkModel> = {}, options?: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  const { entity: trackingLink }: { entity: Partial<TrackingLinkModel>} = useLatestEntity(initEntity, 'trackingLinks')
  const { id } = trackingLink

  const dispatch = useDispatch()

  const pageContext = useContext(PageContext)
  const { callbacks: { showCreateOrEditTrackingLinkModal } } = pageContext

  const { relatedEntity } = useRelations(trackingLink)

  useReduxAction(
    'trackingLinks',
    'loadEntity',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.trackingLinks)

  return {
    callbacks: {
      createOrEditTrackingLink: () => createOrEditTrackingLink({ trackingLink, showCreateOrEditTrackingLinkModal }),
      createTrackingLink: (trackingLinkParams: Partial<TrackingLinkModel>, entityOptions?: TrackingLinkRequestOptions) => (
        createTrackingLink({ trackingLinkParams, dispatch, requestOptions: entityOptions })
      ),
      // @ts-expect-error TS2322
      deleteTrackingLink: () => deleteTrackingLink({ dispatch, trackingLink }),
      updateTrackingLink: (trackingLinkParams: Partial<TrackingLinkModel>, entityOptions?: TrackingLinkRequestOptions) => (
        updateTrackingLink({
          trackingLink, trackingLinkParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    trackingLink,
    loading,
    updating,
    urls: generateUrls(trackingLink),
  }
}

export default useTrackingLink
